import moment from 'moment';

import {
  getFormValue,
  getQuestion,
  setMinDate,
  setMaxDate,
  setMessageVisibility,
  setSectionMessage,
  setSectionMessageVisibility,
  setInitialFocusedDate,
} from 'app/services/businessFunctionHelpers';
import {
  FieldsEnum,
  FormsEnum,
  LabelEnum,
  MessageTypeEnum,
  MessagesEnum,
  SectionsEnum,
} from 'app/shared/enums';
import getNounVarietyByNumber from 'app/shared/helpers/getNounVarietyByNumber';
import { setGracePeriodInfoMessagesVisibility } from './currentlyInPoland';

const formName = FormsEnum.TravelForm;

export const getReturnDateBounds = (arriveDate: string) => {
  let date = moment(arriveDate);

  if (!date.isValid()) {
    date = moment();
  }

  return {
    minDate: date.format('YYYY-MM-DD'),
    maxDate: moment(date)
      .add(1, 'year')
      .subtract(1, 'days')
      .format('YYYY-MM-DD'),
  };
};

const setReturnMinAndMaxDate = (arriveDate: string) => {
  const { minDate, maxDate } = getReturnDateBounds(arriveDate);

  setMinDate({
    key: FieldsEnum.TravelEndDate,
    minDate,
    formName,
  });
  setMaxDate({
    key: FieldsEnum.TravelEndDate,
    maxDate,
    formName,
  });
};

const showNumberOfTravelDays = (isDateValid: boolean) => {
  const arriveDate = getFormValue(FieldsEnum.TravelStartDate, formName);
  const returnDate = getFormValue(FieldsEnum.TravelEndDate, formName);

  if (!arriveDate || !returnDate || !isDateValid) {
    setSectionMessageVisibility({
      formName,
      key: 'travelInsurancePeriod',
      visible: false,
    });

    return;
  }

  const numOfDays = moment(returnDate).diff(arriveDate, 'days') + 1;

  let suffix = getNounVarietyByNumber({
    number: numOfDays,
    noun: LabelEnum.InsurancePeriod2Plural,
    nounFirst: LabelEnum.InsurancePeriod2,
    nounPlural: LabelEnum.InsurancePeriod2Plural,
  });

  const insurancePeriodMessageContent = `${LabelEnum.InsurancePeriod1} ${numOfDays} ${suffix} ${LabelEnum.InsurancePeriod3}`;

  setSectionMessage({
    formName: FormsEnum.TravelForm,
    section: SectionsEnum.TravelInsurancePeriod,
    message: {
      key: 'travelInsurancePeriod',
      visible: Boolean(numOfDays),
      content: insurancePeriodMessageContent,
      type: MessageTypeEnum.Info,
    },
  });
};

const checkMaxRangeExceeded = () => {
  const arriveDate = getFormValue(FieldsEnum.TravelStartDate, formName);
  const returnDate = getFormValue(FieldsEnum.TravelEndDate, formName);

  const numOfDays = moment(returnDate).diff(arriveDate, 'days') + 1;

  return numOfDays > 365;
};

export const travelLength = {
  changeArriveDate() {
    const arriveDate = getFormValue(FieldsEnum.TravelStartDate, formName);
    const returnDate = getFormValue(FieldsEnum.TravelEndDate, formName);
    const currentlyInPoland = getFormValue(
      FieldsEnum.TravelAllTravelersCurrentlyInPoland,
      formName
    );
    const { maxDate } = getQuestion(FieldsEnum.TravelStartDate, formName);

    const today = moment();

    const isDatePast = moment(arriveDate).isBefore(today, 'days');

    const isDateDistant = moment(arriveDate).isAfter(maxDate, 'days');

    const isArriveDateGreaterThanReturn = moment(arriveDate).isAfter(
      returnDate
    );

    const isDateValid =
      !isDateDistant && !isDatePast && !isArriveDateGreaterThanReturn;

    setGracePeriodInfoMessagesVisibility(arriveDate, currentlyInPoland);

    setMessageVisibility({
      key: MessagesEnum.TravelInsuranceStartDatePastNotAllowed,
      visible: isDatePast,
      formName,
    });
    setMessageVisibility({
      key: MessagesEnum.TravelInsuranceStartDateDistantNotAllowed,
      visible: isDateDistant,
      formName,
    });
    setMessageVisibility({
      key: MessagesEnum.TravelStartDateError,
      visible: isArriveDateGreaterThanReturn,
      formName,
    });
    setMessageVisibility({
      key: MessagesEnum.TravelEndDateError,
      visible: false,
      formName,
    });
    setMessageVisibility({
      key: MessagesEnum.TravelInsuranceMaxRangeExceeded,
      visible: checkMaxRangeExceeded(),
      formName,
    });

    showNumberOfTravelDays(isDateValid);
    if (isDateValid) {
      setReturnMinAndMaxDate(arriveDate);
    }

    setInitialFocusedDate({
      key: FieldsEnum.TravelStartDate,
      initialFocusedDate: arriveDate && isDateValid ? arriveDate : '',
      formName,
    });
  },

  changeReturnDate() {
    const arriveDate = getFormValue(FieldsEnum.TravelStartDate, formName);
    const returnDate = getFormValue(FieldsEnum.TravelEndDate, formName);
    const { maxDate } = getQuestion(FieldsEnum.TravelEndDate, formName);

    const today = moment();

    const isDatePast = moment(returnDate).isBefore(today, 'days');

    const isDateDistant = moment(returnDate).isAfter(maxDate, 'days');

    const isArriveDateGreaterThanReturn = moment(arriveDate).isAfter(
      returnDate
    );

    const isDateValid =
      !isDateDistant && !isDatePast && !isArriveDateGreaterThanReturn;

    setMessageVisibility({
      key: MessagesEnum.TravelInsuranceEndDatePastNotAllowed,
      visible: isDatePast,
      formName,
    });
    setMessageVisibility({
      key: MessagesEnum.TravelInsuranceMaxRangeExceeded,
      visible: checkMaxRangeExceeded(),
      formName,
    });
    setMessageVisibility({
      key: MessagesEnum.TravelEndDateError,
      visible: isArriveDateGreaterThanReturn,
      formName,
    });
    setMessageVisibility({
      key: MessagesEnum.TravelStartDateError,
      visible: false,
      formName,
    });

    showNumberOfTravelDays(isDateValid);

    setInitialFocusedDate({
      key: FieldsEnum.TravelEndDate,
      initialFocusedDate: returnDate && isDateValid ? returnDate : '',
      formName,
    });
  },
};
